import React, { useRef, useState, useEffect } from "react";
import { Box, Paper, Popper, Typography, Grow, useTheme } from "@mui/material";

type InfoPopoverProps = {
  title: string;
  description: string;
  children: React.ReactNode;
  placement?:
    | "top-start" | "top" | "top-end"
    | "right-start" | "right" | "right-end"
    | "bottom-start" | "bottom" | "bottom-end"
    | "left-start" | "left" | "left-end";
  enterDelay?: number; // ms
  leaveDelay?: number; // ms
};

const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  description,
  children,
  placement = "top",
  enterDelay = 120,
  leaveDelay = 120,
}) => {
  const theme = useTheme();
  const ref = useRef<HTMLSpanElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const enterTimer = useRef<number | null>(null);
  const leaveTimer = useRef<number | null>(null);

  const handleEnter = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    enterTimer.current = window.setTimeout(() => {
      setAnchorEl(ref.current);
      setOpen(true);
    }, enterDelay);
  };

  const handleLeave = () => {
    if (enterTimer.current) window.clearTimeout(enterTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      setOpen(false);
    }, leaveDelay);
  };

  const handleFocus = () => handleEnter();
  const handleBlur = () => handleLeave();

  useEffect(() => {
    return () => {
      if (enterTimer.current) window.clearTimeout(enterTimer.current);
      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    };
  }, []);

  // Define a origem da animação conforme o placement
  const getTransformOrigin = (p: string | undefined) => {
    if (!p) return "center top";
    if (p.startsWith("top")) return "center bottom";
    if (p.startsWith("bottom")) return "center top";
    if (p.startsWith("left")) return "right center";
    if (p.startsWith("right")) return "left center";
    return "center top";
  };

  // Se o usuário preferir reduzir animações, o Grow respeita o sistema;
  // ainda assim deixamos o timeout curto
  const transitionDuration = 180;

  return (
    <>
      <Box
        component="span"
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "default",
          userSelect: "none",
        }}
        aria-describedby={open ? "info-popover" : undefined}
      >
        {children}
      </Box>

      <Popper
        id="info-popover"
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {({ TransitionProps, placement: currentPlacement }) => (
          <Grow
            {...TransitionProps}
            timeout={transitionDuration}
            style={{
              transformOrigin: getTransformOrigin(
                (currentPlacement as string) ?? placement
              ),
            }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 2,
                maxWidth: 280,
                borderRadius: 2,
                // leve suavização adicional na opacidade em conjunto com o Grow
                // (Grow já anima scale+opacity; isso só garante suavidade no mount)
                "&[data-popper-reference-hidden='true']": { opacity: 0 },
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default InfoPopover;
